import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  LoadingState,
  Page,
  PageHeader,
  PageSection,
  Select,
} from '@/components';
import { useTemplatesByChannel } from '@/hooks';
import {
  type Flow,
  type FlowChannel,
  type FlowStep,
  type FlowStepType,
  type FlowTriggerType,
} from '@/services/flowsService';
import { useFlow, useTestFlow, useUpdateFlow } from '@/hooks/useFlows';

const stepTypeOptions: { value: FlowStepType; label: string }[] = [
  { value: 'ai_interpret', label: 'IA: Interpretar' },
  { value: 'tool_sql', label: 'Tool: SQL' },
  { value: 'tool_api', label: 'Tool: API' },
  { value: 'send_message', label: 'Enviar mensagem' },
  { value: 'condition', label: 'Condição (if/else)' },
  { value: 'delay', label: 'Aguardar (delay)' },
  { value: 'end', label: 'Fim' },
];

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

function safeJsonParse(text: string): { ok: true; value: any } | { ok: false; error: string } {
  try {
    if (!text.trim()) return { ok: true, value: {} };
    return { ok: true, value: JSON.parse(text) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'JSON inválido' };
  }
}

export function FlowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const query = useFlow(id);
  const update = useUpdateFlow(id || '');
  const test = useTestFlow(id || '');

  const [draft, setDraft] = React.useState<Flow | null>(null);

  const waTemplates = useTemplatesByChannel({ channel: 'whatsapp' as any });
  const webTemplates = useTemplatesByChannel({ channel: 'webchat' as any });

  const [simChannel, setSimChannel] = React.useState<FlowChannel>('whatsapp');
  const [simInputType, setSimInputType] = React.useState<'text' | 'audio'>('text');
  const [simText, setSimText] = React.useState('Quanto tem do produto X?');
  const [simMediaId, setSimMediaId] = React.useState('mock-media-id');
  const [simTranscription, setSimTranscription] = React.useState('');

  React.useEffect(() => {
    if (query.data) setDraft(clone(query.data));
  }, [query.data]);

  const setStep = (index: number, next: FlowStep) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const copy = clone(prev);
      copy.steps[index] = next;
      return copy;
    });
  };

  const addStep = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const copy = clone(prev);
      copy.steps.push({ id: `step_${copy.steps.length + 1}`, type: 'end' });
      return copy;
    });
  };

  const removeStep = (index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const copy = clone(prev);
      copy.steps.splice(index, 1);
      return copy;
    });
  };

  const onSave = async () => {
    if (!draft || !id) return;
    try {
      await update.mutateAsync({
        name: draft.name,
        status: draft.status,
        trigger_config: draft.trigger_config,
        steps: draft.steps,
      });
      toast.success('Fluxo salvo');
    } catch {
      toast.error('Não foi possível salvar');
    }
  };

  const onSimulate = async () => {
    if (!id) return;
    try {
      const payload =
        simInputType === 'text'
          ? { channel: simChannel, input: { type: 'text', text: simText } }
          : { channel: simChannel, input: { type: 'audio', media_id: simMediaId, transcription: simTranscription || undefined } };

      const res = await test.mutateAsync(payload as any);
      toast.success('Simulação concluída');
      return res;
    } catch {
      toast.error('Falha ao simular');
    }
  };

  const [lastTrace, setLastTrace] = React.useState<any>(null);

  const handleSimulate = async () => {
    const res = await onSimulate();
    if (res) setLastTrace(res);
  };

  if (query.isLoading) {
    return (
      <Page>
        <PageHeader title="Fluxo" description="Carregando..." />
        <PageSection>
          <LoadingState title="Carregando fluxo" />
        </PageSection>
      </Page>
    );
  }

  if (query.isError) {
    return (
      <Page>
        <PageHeader title="Fluxo" description="Erro ao carregar" right={<Button variant="secondary" onClick={() => navigate('/flows')}>Voltar</Button>} />
        <PageSection>
          <ErrorState title="Falha ao carregar fluxo" onRetry={() => query.refetch()} />
        </PageSection>
      </Page>
    );
  }

  if (!draft) {
    return (
      <Page>
        <PageHeader title="Fluxo" description="Não encontrado" right={<Button variant="secondary" onClick={() => navigate('/flows')}>Voltar</Button>} />
        <PageSection>
          <EmptyState title="Fluxo não encontrado" description="Volte para a lista de fluxos." />
        </PageSection>
      </Page>
    );
  }

  const triggerType = draft.trigger_type as FlowTriggerType;
  const lastRun = lastTrace as any;

  return (
    <Page>
      <PageHeader
        title={draft.name}
        description="Editor por etapas (WhatsApp + Web Chat)"
        right={
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={() => navigate('/flows')}>
              Voltar
            </Button>
            <Button variant="primary" onClick={onSave} disabled={update.isLoading}>
              Salvar
            </Button>
          </div>
        }
      />

      <PageSection>
        <div className="content-grid">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Configuração</div>
                <Badge variant={draft.status === 'active' ? 'success' : draft.status === 'paused' ? 'warning' : 'neutral'}>
                  {draft.status}
                </Badge>
              </div>
            </CardHeader>
            <CardBody>
              <div className="flow-grid-2">
                <FormField label="Nome">
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </FormField>

                <FormField label="Status">
                  <Select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as any })}>
                    <option value="draft">draft</option>
                    <option value="active">active</option>
                    <option value="paused">paused</option>
                  </Select>
                </FormField>

                <FormField label="Trigger">
                  <Select
                    value={triggerType}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === 'inbound') {
                        setDraft({
                          ...draft,
                          trigger_type: 'inbound',
                          trigger_config: { channels: ['whatsapp', 'webchat'] },
                        });
                      } else {
                        setDraft({
                          ...draft,
                          trigger_type: 'schedule',
                          trigger_config: { timezone: 'America/Sao_Paulo', cron: '0 8 * * *' },
                        });
                      }
                    }}
                  >
                    <option value="inbound">inbound</option>
                    <option value="schedule">schedule</option>
                  </Select>
                </FormField>

                {triggerType === 'inbound' ? (
                  <FormField label="Canais">
                    <div className="flow-chips">
                      <label className="flow-chip">
                        <input
                          type="checkbox"
                          checked={draft.trigger_type === 'inbound' && draft.trigger_config?.channels?.includes('whatsapp')}
                          onChange={(e) => {
                            if (draft.trigger_type !== 'inbound') return;
                            const next = new Set(draft.trigger_config?.channels || []);
                            e.target.checked ? next.add('whatsapp') : next.delete('whatsapp');
                            setDraft({ ...draft, trigger_config: { ...draft.trigger_config, channels: Array.from(next) as any } });
                          }}
                        />
                        <span className="text-sm">WhatsApp</span>
                      </label>
                      <label className="flow-chip">
                        <input
                          type="checkbox"
                          checked={draft.trigger_type === 'inbound' && draft.trigger_config?.channels?.includes('webchat')}
                          onChange={(e) => {
                            if (draft.trigger_type !== 'inbound') return;
                            const next = new Set(draft.trigger_config?.channels || []);
                            e.target.checked ? next.add('webchat') : next.delete('webchat');
                            setDraft({ ...draft, trigger_config: { ...draft.trigger_config, channels: Array.from(next) as any } });
                          }}
                        />
                        <span className="text-sm">Web Chat</span>
                      </label>
                    </div>
                  </FormField>
                ) : (
                  <>
                    <FormField label="Timezone">
                      <Input
                        value={draft.trigger_type === 'schedule' ? draft.trigger_config?.timezone : 'America/Sao_Paulo'}
                        onChange={(e) => {
                          if (draft.trigger_type !== 'schedule') return;
                          setDraft({ ...draft, trigger_config: { ...draft.trigger_config, timezone: e.target.value } });
                        }}
                      />
                    </FormField>
                    <FormField label="Cron">
                      <Input
                        value={draft.trigger_type === 'schedule' ? draft.trigger_config?.cron : '0 8 * * *'}
                        onChange={(e) => {
                          if (draft.trigger_type !== 'schedule') return;
                          setDraft({ ...draft, trigger_config: { ...draft.trigger_config, cron: e.target.value } });
                        }}
                      />
                    </FormField>
                  </>
                )}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Simulador</div>
                <Badge variant="neutral">WhatsApp / WebChat</Badge>
              </div>
            </CardHeader>
            <CardBody>
              <div className="flow-grid-2">
                <FormField label="Canal">
                  <Select value={simChannel} onChange={(e) => setSimChannel(e.target.value as any)}>
                    <option value="whatsapp">whatsapp</option>
                    <option value="webchat">webchat</option>
                  </Select>
                </FormField>

                <FormField label="Tipo de input">
                  <Select value={simInputType} onChange={(e) => setSimInputType(e.target.value as any)}>
                    <option value="text">text</option>
                    <option value="audio">audio</option>
                  </Select>
                </FormField>
              </div>

              <div className="flow-mt-12">
                {simInputType === 'text' ? (
                  <FormField label="Mensagem">
                    <Input value={simText} onChange={(e) => setSimText(e.target.value)} />
                  </FormField>
                ) : (
                  <div className="flow-grid-2">
                    <FormField label="media_id">
                      <Input value={simMediaId} onChange={(e) => setSimMediaId(e.target.value)} />
                    </FormField>
                    <FormField label="transcription (opcional)">
                      <Input value={simTranscription} onChange={(e) => setSimTranscription(e.target.value)} />
                    </FormField>
                  </div>
                )}
              </div>

              <div className="flow-actions">
                <Button variant="secondary" onClick={handleSimulate} disabled={test.isLoading}>
                  Rodar simulação
                </Button>
              </div>

              {lastRun?.result?.message ? (
                <div className="flow-result">
                  <div className="flow-result__header">
                    <div className="text-sm font-medium">Resultado</div>
                    <Badge variant="neutral">{String(lastRun?.result?.channel ?? simChannel)}</Badge>
                  </div>
                  <div className="flow-result__message">{String(lastRun.result.message)}</div>
                </div>
              ) : null}

              {lastRun?.trace ? (
                <div className="flow-trace">
                  <details className="flow-details">
                    <summary className="flow-details__summary">
                      <div className="flow-trace__header">
                        <div className="text-sm font-medium">Trace</div>
                        <Badge variant="neutral">{Array.isArray(lastRun.trace) ? `${lastRun.trace.length} steps` : 'trace'}</Badge>
                      </div>
                    </summary>
                    <div className="flow-details__content">
                      <pre className="code-block code-block--compact">{JSON.stringify(lastRun, null, 2)}</pre>
                    </div>
                  </details>
                </div>
              ) : null}
            </CardBody>
          </Card>
        </div>
      </PageSection>

      <PageSection>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Etapas</div>
              <Button variant="secondary" onClick={addStep}>
                Adicionar etapa
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flow-steps">
              {draft.steps.map((s, idx) => {
                const paramsText = JSON.stringify(s.params ?? {}, null, 2);
                const typeLabel = stepTypeOptions.find((o) => o.value === s.type)?.label ?? s.type;

                return (
                  <div key={s.id} className="flow-step">
                    <div className="flow-step__header">
                      <div className="flow-step__title">
                        <Badge variant="neutral">#{idx + 1}</Badge>
                        <div className="flow-step__title-text">
                          <div className="text-sm font-medium">{typeLabel}</div>
                          <div className="text-xs text-gray-500">{s.id}</div>
                        </div>
                      </div>

                      <div className="flow-step__header-actions">
                        <Select
                          value={s.type}
                          onChange={(e) => setStep(idx, { ...s, type: e.target.value as any })}
                        >
                          {stepTypeOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                        <Button variant="secondary" onClick={() => removeStep(idx)}>
                          Remover
                        </Button>
                      </div>
                    </div>

                    <div className="flow-step__body">
                      <FormField label="Nome (opcional)">
                        <Input value={s.name ?? ''} onChange={(e) => setStep(idx, { ...s, name: e.target.value })} />
                      </FormField>

                      {s.type === 'send_message' ? (
                        (() => {
                          const stepChannel = ((s.params?.channel as string) ?? 'whatsapp') as 'whatsapp' | 'webchat' | '{{channel}}';
                          const resolvedChannel = stepChannel === '{{channel}}' ? 'whatsapp' : stepChannel;
                          const templates =
                            resolvedChannel === 'webchat' ? (webTemplates.data ?? []) : (waTemplates.data ?? []);

                          return (
                        <div className="flow-grid-2">
                          <FormField label="Canal">
                            <Select
                              value={(s.params?.channel as string) ?? '{{channel}}'}
                              onChange={(e) => setStep(idx, { ...s, params: { ...(s.params ?? {}), channel: e.target.value } })}
                            >
                              <option value="{{channel}}">{'{{channel}} (auto)'}</option>
                              <option value="whatsapp">whatsapp</option>
                              <option value="webchat">webchat</option>
                            </Select>
                          </FormField>

                          <FormField label="Modo">
                            <Select
                              value={(s.params?.mode as string) ?? 'template'}
                              onChange={(e) => {
                                const mode = e.target.value;
                                const nextParams: any = { ...(s.params ?? {}), mode };
                                if (mode === 'template') {
                                  delete nextParams.text;
                                } else {
                                  delete nextParams.template_id;
                                }
                                setStep(idx, { ...s, params: nextParams });
                              }}
                            >
                              <option value="template">template</option>
                              <option value="text">text</option>
                            </Select>
                          </FormField>

                          {((s.params?.mode as string) ?? 'template') === 'template' ? (
                            <FormField label="Template">
                              <Select
                                value={(s.params?.template_id as string) ?? ''}
                                onChange={(e) => setStep(idx, { ...s, params: { ...(s.params ?? {}), template_id: e.target.value } })}
                              >
                                <option value="">Selecione...</option>
                                {templates.map((t: any) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                  </option>
                                ))}
                              </Select>
                            </FormField>
                          ) : (
                            <FormField label="Texto">
                              <Input
                                value={(s.params?.text as string) ?? ''}
                                placeholder="Ex: Olá! Como posso ajudar?"
                                onChange={(e) => setStep(idx, { ...s, params: { ...(s.params ?? {}), text: e.target.value } })}
                              />
                            </FormField>
                          )}
                        </div>
                          );
                        })()
                      ) : null}

                      <details className="flow-details">
                        <summary className="flow-details__summary">
                          <div className="text-sm font-medium">Params (JSON)</div>
                        </summary>
                        <div className="flow-details__content">
                          <textarea
                            className="textarea"
                            rows={5}
                            defaultValue={paramsText}
                            onBlur={(e) => {
                              const parsed = safeJsonParse(e.target.value);
                              if (!parsed.ok) {
                                toast.error(`Params inválidos: ${parsed.error}`);
                                return;
                              }
                              setStep(idx, { ...s, params: parsed.value });
                            }}
                          />
                        </div>
                      </details>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      </PageSection>
    </Page>
  );
}
